import React, { useEffect, useState, useCallback } from 'react';
import { eventCustomersAPI, configAPI } from '../services/api';

interface EventCustomerModalProps {
  uuid: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  eventId: number;
}

const EventCustomerModal: React.FC<EventCustomerModalProps> = ({ uuid, isOpen, onClose, onSave, eventId }) => {
  const [form, setForm] = useState({
    number_of_tickets_adults: 0,
    number_of_tickets_kids: 0,
  });
  const [eventCustomer, setEventCustomer] = useState<any>(null);
  const [eventDetails, setEventDetails] = useState<any>(null);
  const [calculatedAmount, setCalculatedAmount] = useState<number>(0);
  const [discountConfig, setDiscountConfig] = useState<{ threshold: number; rate: number }>({
    threshold: 0,
    rate: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const calculateAmount = useCallback(() => {
    if (!eventDetails) return;

    const adults = parseInt(form.number_of_tickets_adults.toString()) || 0;
    const kids = parseInt(form.number_of_tickets_kids.toString()) || 0;
    const totalTickets = adults + kids;

    const adultPrice = eventDetails.event_ticket_price_adults || 0;
    const kidPrice = eventDetails.event_ticket_price_kids || 0;
    
    let amount = (adults * adultPrice) + (kids * kidPrice);

    // Apply discount if eligible
    if (eventDetails.discount_allowed && totalTickets >= discountConfig.threshold && discountConfig.rate > 0) {
      amount = amount * (1 - discountConfig.rate / 100);
    }

    setCalculatedAmount(amount);
  }, [eventDetails, form.number_of_tickets_adults, form.number_of_tickets_kids, discountConfig]);

  const fetchEventCustomerAndConfig = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch event customers for this event
      const response = await eventCustomersAPI.getByEventId(eventId);
      const customer = response.data.eventCustomers.find((ec: any) => ec.uuid === uuid);
      
      if (customer) {
        setEventCustomer(customer);
        setForm({
          number_of_tickets_adults: customer.number_of_tickets_adults || 0,
          number_of_tickets_kids: customer.number_of_tickets_kids || 0,
        });

        // Set event details from the customer record (populated via backend join)
        setEventDetails({
          event_ticket_price_adults: Number(customer.event_ticket_price_adults) || 0,
          event_ticket_price_kids: Number(customer.event_ticket_price_kids) || 0,
          discount_allowed: !!customer.discount_allowed,
          title: customer.event_title,
          uuid: customer.event_uuid,
        });
      }

      // Fetch discount config
      const thresholdResponse = await configAPI.getByKey('TICKET_DISCOUNT_THRESHOLD');
      const rateResponse = await configAPI.getByKey('TICKET_DISCOUNT_RATE');
      
      setDiscountConfig({
        threshold: parseFloat(thresholdResponse.data.value) || 0,
        rate: parseFloat(rateResponse.data.value) || 0
      });

    } catch (err) {
      setError('Failed to load event customer details');
    } finally {
      setLoading(false);
    }
  }, [eventId, uuid]);

  useEffect(() => {
    if (isOpen && uuid) {
      fetchEventCustomerAndConfig();
    } else {
      setForm({ number_of_tickets_adults: 0, number_of_tickets_kids: 0 });
      setError('');
      setCalculatedAmount(0);
    }
  }, [isOpen, uuid, fetchEventCustomerAndConfig]);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Recalculate amount when ticket counts or config change
  useEffect(() => {
    if (eventDetails) {
      calculateAmount();
    }
  }, [calculateAmount, eventDetails]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      await eventCustomersAPI.update(uuid, form);
      onSave();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update event customer');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isStripePaid = eventCustomer?.mode === 'stripe';
  const oldTotalTickets = (eventCustomer?.number_of_tickets_adults || 0) + (eventCustomer?.number_of_tickets_kids || 0);
  const newTotalTickets = (form.number_of_tickets_adults || 0) + (form.number_of_tickets_kids || 0);
  const ticketsChanged = oldTotalTickets !== newTotalTickets;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-primary-600 to-secondary-600 px-8 py-6 rounded-t-2xl">
          <h2 className="text-2xl font-bold text-white">
            {isStripePaid ? 'View Event Customer' : 'Edit Event Customer'}
          </h2>
        </div>

        <div className="p-8">
          {loading && !eventCustomer ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-secondary-600 mt-4">Loading...</p>
            </div>
          ) : (
            <>
              {eventCustomer && (
                <div className="mb-6 p-4 bg-secondary-50 rounded-lg">
                  <p className="text-secondary-700"><strong>Customer:</strong> {eventCustomer.customer_name}</p>
                  <p className="text-secondary-700"><strong>Email:</strong> {eventCustomer.email}</p>
                </div>
              )}

              {isStripePaid && (
                <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
                  <p className="font-semibold text-blue-800">💳 Stripe Payment — Editing Disabled</p>
                  <p className="text-blue-700 text-sm mt-1">
                    This booking was paid via Stripe and cannot be edited by an admin. To change ticket
                    quantities or amounts, the customer must manage their own booking, or a refund must
                    be processed through Stripe.
                  </p>
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Number of Adult Tickets
                  </label>
                  <input
                    type="number"
                    name="number_of_tickets_adults"
                    value={form.number_of_tickets_adults}
                    onChange={handleChange}
                    min="0"
                    disabled={isStripePaid}
                    className={`w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      isStripePaid ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                    }`}
                  />
                  {eventDetails && (
                    <p className="text-sm text-secondary-500 mt-1">
                      Price: ${Number(eventDetails.event_ticket_price_adults || 0).toFixed(2)} per adult ticket
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Number of Kid Tickets
                  </label>
                  <input
                    type="number"
                    name="number_of_tickets_kids"
                    value={form.number_of_tickets_kids}
                    onChange={handleChange}
                    min="0"
                    disabled={isStripePaid}
                    className={`w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      isStripePaid ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                    }`}
                  />
                  {eventDetails && (
                    <p className="text-sm text-secondary-500 mt-1">
                      Price: ${Number(eventDetails.event_ticket_price_kids || 0).toFixed(2)} per kid ticket
                    </p>
                  )}
                </div>

                {/* Calculated Amount Display */}
                <div className="p-4 bg-primary-50 border-2 border-primary-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-secondary-700 font-medium">Total Tickets:</span>
                    <span className="text-secondary-900 font-bold">{newTotalTickets}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-secondary-700 font-medium">Calculated Amount:</span>
                    <span className="text-primary-700 font-bold text-xl">
                      ${calculatedAmount.toFixed(2)}
                    </span>
                  </div>
                  {eventDetails?.discount_allowed && newTotalTickets >= discountConfig.threshold && discountConfig.rate > 0 && (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ Discount applied ({discountConfig.rate}% off for {discountConfig.threshold}+ tickets)
                    </p>
                  )}
                </div>

                {/* Warning if tickets changed */}
                {ticketsChanged && (
                  <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                    <p className="text-yellow-800 font-medium">⚠️ Ticket Count Changed</p>
                    <p className="text-yellow-700 text-sm mt-1">
                      Old tickets will be invalidated and the customer will be notified by email. 
                      New tickets will need to be generated separately.
                    </p>
                  </div>
                )}

                {error && (
                  <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
                    {error}
                  </div>
                )}
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 bg-secondary-200 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-300 transition-all"
                  disabled={loading}
                >
                  {isStripePaid ? 'Close' : 'Cancel'}
                </button>
                {!isStripePaid && (
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventCustomerModal;
